/**
 * Servicio para cargar configuración desde archivo JSON
 * Este archivo se genera dinámicamente en el pipeline de deployment
 */

export interface AuthConfiguration {
    clientId: string;
    authority: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
}

export interface ApiConfiguration {
    flightChanges?: string;
    // Add new APIs here as needed
}

export interface AppConfiguration {
    environment: string;
    endpoint: string;
    auth: AuthConfiguration;
    apis?: ApiConfiguration;
}

class ConfigService {
    private config: AppConfiguration | null = null;
    private loadPromise: Promise<AppConfiguration> | null = null;

    /**
     * Carga la configuración desde el archivo JSON
     * Utiliza cache para evitar múltiples llamadas
     */
    async loadConfiguration(): Promise<AppConfiguration> {
        // Si ya tenemos la configuración cargada, la retornamos
        if (this.config) {
            return this.config;
        }

        // Si ya hay una promesa de carga en progreso, la retornamos
        if (this.loadPromise) {
            return this.loadPromise;
        }

        // Crear nueva promesa de carga
        this.loadPromise = this.fetchConfiguration();
        
        try {
            this.config = await this.loadPromise;
            return this.config;
        } catch (error) {
            // Reset promise en caso de error para permitir reintentos
            this.loadPromise = null;
            throw error;
        }
    }

    /**
     * Obtiene la configuración de autenticación
     */
    async getAuthConfig(): Promise<AuthConfiguration> {
        const config = await this.loadConfiguration();
        return config.auth;
    }

    async getAppConfig(): Promise<AppConfiguration> {
        return this.loadConfiguration();
    }

    private async fetchConfiguration(): Promise<AppConfiguration> {
        try {
            const response = await fetch('/assets/config/configuration.json');
            
            if (!response.ok) {
                throw new Error(`Failed to load configuration: ${response.status} ${response.statusText}`);
            }

            const config: AppConfiguration = await response.json();
            
            this.validateConfiguration(config);
            
            config.auth.redirectUri = config.auth.redirectUri || window.location.origin;
            config.auth.postLogoutRedirectUri = config.auth.postLogoutRedirectUri || window.location.origin;
            
            console.log('Configuration loaded successfully:', {
                environment: config.environment,
                endpoint: config.endpoint,
                clientId: config.auth.clientId ? config.auth.clientId.substring(0, 8) + '...' : 'not set'
            });
            
            return config;
        } catch (error) {
            console.error('Error loading configuration:', error);
            // Default configuration fallback in case of error
            return this.getDefaultConfiguration();

        }
    }


    private validateConfiguration(config: any): void {
        if (!config) {
            throw new Error('Configuration is null or undefined');
        }

        if (!config.auth) {
            throw new Error('Configuration missing auth section');
        }

        if (!config.auth.clientId) {
            throw new Error('Configuration missing auth.clientId');
        }

        if (!config.auth.authority) {
            throw new Error('Configuration missing auth.authority');
        }
    }

    private getDefaultConfiguration(): AppConfiguration {
        console.warn('Using default configuration as fallback');
        
        return {
            environment: 'development',
            endpoint: 'https://endpoint.test',
            auth: {
                clientId: "",
                authority: "",
                redirectUri: window.location.origin,
                postLogoutRedirectUri: window.location.origin,
            }
        };
    }

    clearCache(): void {
        this.config = null;
        this.loadPromise = null;
    }
}

export const configService = new ConfigService();